import React, { useState } from 'react';
import classes from './LinkedFlows.module.scss';
import { Stack } from 'new/elements/Stack';
import { ResourceSelectionButton } from 'new/elements/ResourceSelectionButton/ResourceSelectionButton';
import { RESOURCE_TYPE_FLOW } from 'js/constants/resourceTypes';
import useFlowTriggersAdapter from 'new/state/flowTriggers/adapter/useFlowTriggersAdapter';
import { FlowLinksInput } from 'new/state/flowTriggers/types';
import { tryCatchAsync } from 'new/utils/TryCatch';
import useRequests from 'new/state/request/repository/useRequests';
import useEntities from 'new/state/entities/repository/useEntities';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import { getResourceEntity } from 'js/lib/PipelineUtils';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  CREATE_FLOW_TRIGGERS_REQUEST,
  DELETE_FLOW_TRIGGERS_REQUEST,
  REFRESH_PIPELINE_REQUEST,
} from 'js/constants/actionTypes';
import { differenceBy } from 'lodash';

interface LinkedFlowsProps {
  data: any;
}

export const LinkedFlows = (props: LinkedFlowsProps) => {
  const { data } = props;
  const { createLinks, deleteLinks, deleteAllLinks } = useFlowTriggersAdapter();
  const { refreshPipelineById } = usePipelinesAdapter();

  const isLoading = useIsLoading(
    REFRESH_PIPELINE_REQUEST,
    DELETE_FLOW_TRIGGERS_REQUEST,
    CREATE_FLOW_TRIGGERS_REQUEST,
  );

  const { data: entities } = useEntities();
  const { flowNodes } = entities;

  const defaultSelected = data?.linked_flows?.map((l) => {
    const resource = getResourceEntity(flowNodes[l], entities);
    return {
      ...resource,
      ...flowNodes[l],
      id: resource.id,
    };
  });

  const { showError } = useRequests();
  const [selected, setSelected] = useState<any[]>(defaultSelected);

  async function onChoseFlow(s: any[]) {
    const flowLinks = s.map((flow) => flow.flow_node_id) as FlowLinksInput;
    setSelected(s);

    // To avoid checking on each flow got removed, is easier to just delete all
    // links and add the selected ones
    const { error: deleteError } = await tryCatchAsync(deleteAllLinks(data.flow_node_id));
    if (deleteError) {
      showError('There was an error trying to update your linked flows');
      return;
    }
    const { data: response, error } = await tryCatchAsync(
      createLinks(data.flow_node_id, { linked_flows: flowLinks }),
    );
    if (error) {
      showError('There was an error trying to update your linked flows');
      return;
    }

    // If a flow is removed from the selection page we need to get the diff so
    // we can update the removed node.
    const diff = differenceBy(selected, s, 'flow_node_id');
    for (let i = 0; i < diff.length; i++) {
      await tryCatchAsync(refreshPipelineById(diff[i].flow_node_id, { addToRoot: false }));
    }

    // We also have to update the flows that were added as links
    const linkedFlows = response.result.linked_flows;
    for (let i = 0; i < linkedFlows.length; i++) {
      await tryCatchAsync(refreshPipelineById(linkedFlows[i], { addToRoot: false }));
    }
    // Also update the base flow
    await tryCatchAsync(refreshPipelineById(data.flow_node_id, { addToRoot: false }));
  }

  const onRemove = async (item) => {
    await deleteLinks(data.flow_node_id, { linked_flows: [item.flow_node_id] });
    await refreshPipelineById(data.flow_node_id, { addToRoot: false });
    await refreshPipelineById(item.flow_node_id, { addToRoot: false });
  };

  return (
    <Stack padding="16px" position="relative" height="100%">
      <ResourceSelectionButton
        label="Find Flows to Link"
        multi
        hideDetails
        fixed={false}
        isItemDisabled={(item) => item.flow_node_id === data.flow_node_id}
        defaultSelected={defaultSelected}
        resourceType={RESOURCE_TYPE_FLOW}
        onChoose={onChoseFlow}
        onRemove={onRemove}
      />
      {isLoading ? <CenteredLoader absolute /> : null}
    </Stack>
  );
};
